/**
 * QorbomNav.jsx — shared top navigation for every QorBOM-brand page.
 *
 * Phase 14.3 polish.
 *
 * Every page under the QorBOM brand (homepage, partner application,
 * API docs, trust) renders this same nav so a visitor on any
 * sub-page can always:
 *   • return to the QorBOM homepage (logo OR "Home" link)
 *   • jump to the other QorBOM pages
 *   • cross over to qortrace.com
 *
 * Hostname-aware: when the user is on qorbom.com, internal links use
 * root paths (/become-a-partner, /developers); on qortrace.com it
 * falls back to the legacy `/qorbom/*` paths so the same nav works
 * on the SaaS frontend too.
 */
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";

import QorbomBrand from "./qorbom/QorbomBrand";


// Resolve the right link target for the current hostname.
// • qorbom.com  →  root path (eg. /developers)
// • everywhere else → legacy /qorbom/* path
function useBrandLink() {
  return React.useCallback((rootPath, legacyPath) => {
    if (typeof window === "undefined") return legacyPath;
    const h = window.location.hostname.toLowerCase();
    return /(?:^|\.)qorbom\.com$/.test(h) ? rootPath : legacyPath;
  }, []);
}


export default function QorbomNav({ active }) {
  const link = useBrandLink();
  const homeHref     = link("/", "/qorbom-home");
  const partnerHref  = link("/become-a-partner", "/qorbom/become-a-partner");
  const docsHref     = link("/developers",       "/qorbom/api-docs");
  const trustHref    = link("/qorbom/trust",     "/qorbom/trust");
  const portalHref   = link("/portal",           "/qorbom/portal");

  return (
    <nav
      data-testid="qorbom-shared-nav"
      style={{
        maxWidth: 1280, margin: "0 auto",
        padding: "20px 32px",
        display: "flex", alignItems: "center", gap: 18,
        flexWrap: "wrap",
      }}>
      {/* Logo — primary home anchor (always works) */}
      <Link to={homeHref} data-testid="qorbom-nav-logo"
            style={{
              textDecoration: "none", color: "#0F172A",
              display: "flex", alignItems: "center", gap: 10,
            }}>
        <span style={{
          width: 26, height: 26, borderRadius: 3,
          background: "#0033A0", color: "#FFFFFF",
          display: "inline-flex", alignItems: "center",
          justifyContent: "center",
          fontFamily: "Manrope, sans-serif", fontWeight: 800, fontSize: 14,
        }}>Q</span>
        <span style={{
          fontFamily: "Manrope, sans-serif", fontSize: 17, fontWeight: 700,
        }}>
          <QorbomBrand />
        </span>
      </Link>
      <div style={{ flex: 1 }} />

      {/* Home — second affordance so users on sub-pages don't have
          to guess that the logo is clickable. Surfaces "← Home" on
          every non-home QorBOM page. */}
      {active !== "home" && (
        <Link to={homeHref}
              data-testid="qorbom-nav-home"
              style={navLinkStyle(active === "home")}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#0033A0")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#475569")}>
          <Home size={13} style={{
            display: "inline", verticalAlign: -2, marginRight: 5,
          }} />
          Home
        </Link>
      )}

      <NavItem to={partnerHref}  active={active === "partner"}>Become a partner</NavItem>
      <NavItem to={docsHref}     active={active === "docs"}>API docs</NavItem>
      <NavItem to={trustHref}    active={active === "trust"}>Trust</NavItem>
      <NavItem to={portalHref}   active={active === "portal"}>Portal</NavItem>

      <a href="https://qortrace.com" target="_blank" rel="noreferrer"
         data-testid="qorbom-nav-qortrace"
         style={{
           padding: "7px 14px", borderRadius: 4,
           background: "transparent", color: "#475569",
           border: "1px solid #E2E8F0",
           fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
           letterSpacing: "0.05em", textDecoration: "none",
         }}>
        QORTRACE.COM ↗
      </a>

      <Link to={partnerHref}
            data-testid="qorbom-nav-cta"
            style={{
              padding: "8px 16px", borderRadius: 4,
              background: "#0033A0", color: "#FFFFFF",
              fontWeight: 600, fontSize: 13, textDecoration: "none",
              display: "inline-flex", alignItems: "center", gap: 5,
            }}>
        Apply <ArrowRight size={13} />
      </Link>
    </nav>
  );
}


function NavItem({ to, active, children }) {
  return (
    <Link to={to}
          data-testid={`qorbom-nav-${String(children).toLowerCase().replace(/\s+/g, '-')}`}
          style={navLinkStyle(active)}
          onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = "#0033A0"; }}
          onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = "#475569"; }}>
      {children}
    </Link>
  );
}


function navLinkStyle(active) {
  return {
    fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 13,
    color: active ? "#0033A0" : "#475569",
    textDecoration: "none", fontWeight: active ? 700 : 500,
    padding: "6px 0",
    borderBottom: active ? "2px solid #0033A0" : "2px solid transparent",
    transition: "color 120ms",
  };
}
